import { Card, CardActions, Collapse, IconButton, IconButtonProps, List, ListItem, styled, Typography } from '@mui/material';
import React from 'react';
import ExpandIcon from 'remixicon-react/ArrowDownSFillIcon';
import { ConfigContext } from '../../../../App/App';
import { ChatMessage } from '../../../../Shared/API/ApiTypes';
import MessageSending from '../../MessageSender/MessageSending';
import StructuredDataResultsList from '../StructuredData/StructuredDataResultsList';
import './Message.css';
import MessageContent from './MessageContent';
import FillNotice from '../../../../ConversationalForms/FillNotice';
import FillReceipts from '../../../../ConversationalForms/FillReceipts';
import FillRetryNotice from '../../../../ConversationalForms/FillRetryNotice';
import ReviewSubmitCard from '../../../../ConversationalForms/ReviewSubmitCard';
import StartCard from '../../../../ConversationalForms/StartCard';
import TurnError from '../../../../ConversationalForms/TurnError';
import SlowTurnCaption from '../../../../ConversationalForms/SlowTurnCaption';

interface ExpandMoreProps extends IconButtonProps {
  expand: boolean;
}

const ExpandMore = styled((props: ExpandMoreProps) => {
  const { expand, ...other } = props;
  return <IconButton {...other} sx={{ p: 0, display: 'flex', justifyContent: 'center' }} />;
})(({ theme, expand }) => ({
  transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
  marginLeft: 'auto',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}));

type MessageProps = {
  id?: string;
  message: ChatMessage;
  tabIndex?: number;
  /** Position in the conversation — the conversational-forms retry removes by index. */
  index?: number;
};

const Message = React.forwardRef<HTMLDivElement, MessageProps>(({ id, message, tabIndex = -1, index = -1 }, ref) => {
  const config = React.useContext(ConfigContext);
  const [expanded, setExpanded] = React.useState(false);
  const botName = config?.BotName ?? 'bot';

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  return (
    <div
      className={'messageRow ' + (message.direction === 0 ? 'zero' : 'one')}
      data-testid="chat-message-row"
      id={id}
      ref={ref}
      tabIndex={tabIndex}
    >
      <Card elevation={2} className={`chatMessage${message.proactive ? ' proactiveMessage' : ''}`}
        sx={{
          p: 1,
          background: message.direction === 0 ? `${config.PrimaryColor}` : `white`,
          borderRadius: 2,
          color: message.direction === 0 ? 'white' : 'black',
          border: message.proactive ? `2px solid ${config.SecondaryColor || config.PrimaryColor}` : undefined,
          '&:hover': {
            backgroundColor: message.direction === 0 ? 'primary.light' : 'white'
          }
        }}
      >
        <div className="messageInfo" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
            {/* ADR #55: a proactive message is an ordinary bot message in the open
                chat — no visible "Proactive" marker shown to visitors. */}
            {message.direction === 0 ? 'You' : botName}
            <div>|</div>
            <div>
              {new Date(message.time).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
          {/* ADR #55: no per-message dismiss control in the open chat. A proactive
              message is an ordinary message here; dismissal is implicit (close the
              widget within the window) or the user simply replies. The only explicit
              dismiss affordance is the minimized teaser ✕. */}
        </div>
        <div>
          {message.temp && message.content === "" &&
            <>
              <MessageSending />
              {/* Past the slow-turn threshold the typing bubble gains a
                  reassurance caption; nothing else about the turn changes. */}
              <SlowTurnCaption />
            </>
          }
          {message.formError
            ? <TurnError index={index} content={message.content} retryText={message.retryText} />
            : <MessageContent messageContent={message.content} />}
          {/* Extraction failed unretryably: the reply stands, the fill did not.
              'partial' means receipts render below, so only some were lost. */}
          {message.fillNotice && <FillNotice partial={message.fillNotice === 'partial'} />}
          {/* Engagement closure (store tracker 6.55): the reply above stood on
              its own — this never masks it — but its own fill attempt is
              safely re-runnable per the server's guarantee. Quieter than
              `TurnError`'s full-failure card, and never rendered together
              with it (a `formError` turn has no answer to render at all). */}
          {message.fillRetryText && <FillRetryNotice index={index} retryText={message.fillRetryText} />}
        </div>
        {/* Fill receipts belong to this turn's message (conversational forms). */}
        {message.fills && <FillReceipts fills={message.fills} onUserCard={message.direction === 0} />}
        {message.direction === 1 && message.relatedLinks && message.relatedLinks.length > 0 &&
          <>
            <CardActions disableSpacing sx={{ ml: 0, p: 0, pt: 1 }}
              onClick={handleExpandClick}>
              <Typography color="GrayText" variant='body2' sx={{ fontWeight: 'bold' }}>Related Links</Typography>
              <ExpandMore
                expand={expanded}
                aria-expanded={expanded}
                aria-label="Related Links"
              >
                <ExpandIcon />
              </ExpandMore>
              {/* <Button variant="text" size="small" sx={{ background: 'white', backgroundColor: 'white !important', boxShadow: 'none', p: 0, m: 0 }}>Related Links</Button> */}
            </CardActions>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <List sx={{ fontSize: '.8rem' }}>
                {message.relatedLinks.map((l, i) => {
                  return <ListItem key={i} sx={{ borderLeft: `3px solid ${config.SecondaryColor}`, marginBottom: 1, color: config.PrimaryColor }}><a href={l.url} target="_blank" rel="noreferrer">{l.title}</a></ListItem>
                })}

              </List>
            </Collapse>
          </>
        }
      </Card>
      {message.direction === 1 && message.cards &&
        <StructuredDataResultsList cards={message.cards} startCollapsed={true} />
      }
      {message.formOfferId && <StartCard formId={message.formOfferId} />}
      {message.formCompleteId && <ReviewSubmitCard formId={message.formCompleteId} />}
    </div>
  );
});

Message.displayName = 'Message';

export default Message;
